/**
 * Shared constants for media (image/video/audio) handling across the editor.
 *
 * Covers the Upload panel tabs, which mime types and extensions each media
 * kind accepts, the fabric property names for image layers, fit-box modes,
 * and the scopes a media element can play back in (canvas, gallery, details
 * panel).
 *
 * Used by the Upload/Image/Video/Audio tools, the media library service,
 * attribute/CSV mapping, and the canvas renderers.
 *
 * Mental model: each concern here is one flat object or Set - look up a key,
 * get back the exact string fabric/the UI expects. Nothing here computes
 * anything, it's all static config.
 */
import { BASE_FABRIC_KEYS } from '@/constants/layers';
import type { ImageLayer } from '@/types/layers/layer';
import type { FitOptions, MediaPlaybackScope, MediaTab } from '@/types/layers/media';

export const MEDIA_KIND = {
  Image: 'image',
  Video: 'video',
  Audio: 'audio'
} as const;

/** Anything else is treated as a media name rather than a URL. */
export const USABLE_URL_SCHEMES = new Set([
  'http:',
  'https:',
  'data:',
  'blob:'
]);

export function hasUsableUrlScheme(value: string): boolean {
  try {
    return USABLE_URL_SCHEMES.has(new URL(value.trim()).protocol);
  } catch {
    return false;
  }
}

export const MEDIA_TABS: MediaTab[] = [
  {
    kind: 'images',
    label: 'labels.images',
    icon: 'image',
    emptyMessage: '',
    ready: true
  },
  {
    kind: 'videos',
    label: 'labels.videos',
    icon: 'videocam',
    emptyMessage: 'labels.noVideosUploaded',
    ready: true
  },
  {
    kind: 'audio',
    label: 'labels.audio',
    icon: 'audio_file',
    emptyMessage: 'labels.noAudioUploaded',
    ready: true
  }
];

export const SUPPORTED_MEDIA_FORMATS = {
  image: {
    mimeTypes: new Set([
      'image/jpeg',
      'image/png',
      'image/bmp',
      'image/webp',
      'image/gif',
      'image/avif'
    ]),
    extensions: new Set(['jpg', 'jpeg', 'png', 'bmp', 'webp', 'gif', 'avif'])
  },
  video: {
    mimeTypes: new Set([
      'video/mp4',
      'video/webm',
      'video/quicktime',
      'video/x-matroska'
    ]),
    extensions: new Set(['mp4', 'webm', 'mov', 'mkv'])
  },
  audio: {
    mimeTypes: new Set([
      'audio/mpeg',
      'audio/mp3',
      'audio/wav',
      'audio/x-wav',
      'audio/ogg'
    ]),
    extensions: new Set(['mp3', 'wav', 'ogg'])
  }
};

/** Largest video file (in bytes) accepted by the upload flow — 500 MB. */
export const MAX_VIDEO_UPLOAD_BYTES = 500 * 1024 * 1024;
/** Human-readable counterpart of {@link MAX_VIDEO_UPLOAD_BYTES} for messages. */
export const MAX_VIDEO_UPLOAD_MB = 500;

export const IMAGE_PROPS = {
  URL: 'url',
  IMAGE_ID: 'imageId',
  IMAGE_TYPE: 'imageType',
  ORBIT: 'orbit',
  FLIP_X: 'flipX',
  FLIP_Y: 'flipY',
  FIT_OPTIONS: 'imageFit',
  FIT_BOX_WIDTH: 'fitBoxWidth',
  FIT_BOX_HEIGHT: 'fitBoxHeight',
  BORDER_RADIUS: 'borderRadius',
  BORDER_COLOR: 'borderColor',
  BORDER_WIDTH: 'borderWidth',
  BORDER_DASH_ARRAY: 'borderDashArray',
  BORDER_LINE_JOIN: 'borderLineJoin',
  SHADOW_COLOR: 'shadowColor',
  SHADOW_BLUR: 'shadowBlur',
  SHADOW_OFFSET_X: 'shadowOffsetX',
  SHADOW_OFFSET_Y: 'shadowOffsetY',
  BLEND_ROLE: 'blendRole'
} as const;

export const DEFAULT_SHADOW_COLOR = 'rgba(0,0,0,0.5)';


/**
  * Default Values for Audio and Video Layers
*/
export const DEFAULT_MEDIA_WIDTH = 640;
export const DEFAULT_MEDIA_HEIGHT = 360;
export const DEFAULT_MEDIA_DURATION = 0;

export const FIT_OPTIONS = {
  STRETCH: 1,
  FIT: 2,
  FILL: 3,
  ORIGINAL: 4
} as const satisfies Record<string, FitOptions>;

/** Image-layer properties that map 1:1 to Fabric object props. */
export const IMAGE_FABRIC_KEYS: readonly (keyof ImageLayer)[] = [
  ...BASE_FABRIC_KEYS
];

export const IMAGE_TOOL_ITEMS = [
  {
    id: 1,
    title: 'labels.library',
    key: 'library'
  }
];

export const SUPPORTED_FORMATS = new Set([
  'image/jpeg',
  'image/png',
  'image/bmp',
  'image/gif',
  'image/svg+xml'
]);

export const MEDIA_PLAYBACK_SCOPE = {
  Canvas: 'canvas',
  Gallery: 'gallery',
  Details: 'details'
} as const satisfies Record<string, MediaPlaybackScope>;

/** Allowed media-clock drift before live/scrub playback re-seeks an element. */
export const MEDIA_SEEK_THRESHOLD_SECONDS = {
  live: 0.25,
  scrub: 0.05
} as const;
